import { TFunction } from 'i18next';
import { CounterMeasureTypeEnum, CounterMeasureUnitEnum } from 'graphql-common';

export function getMeasureTypeOptions() {
  return [
    {
      labelKey: 'measure-type-distance',
      value: CounterMeasureTypeEnum.Distance,
    },
    {
      labelKey: 'measure-type-energy',
      value: CounterMeasureTypeEnum.Energy,
    },
    {
      labelKey: 'measure-type-mass',
      value: CounterMeasureTypeEnum.Mass,
    },
    {
      labelKey: 'measure-type-quantity',
      value: CounterMeasureTypeEnum.Quantity,
    },
    {
      labelKey: 'measure-type-time',
      value: CounterMeasureTypeEnum.Time,
    },
  ];
}

export function getMeasureDistanceUnitOptions() {
  return [
    {
      labelKey: 'measure-unit-centimeter',
      value: CounterMeasureUnitEnum.Centimeter,
    },
    {
      labelKey: 'measure-unit-meter',
      value: CounterMeasureUnitEnum.Meter,
    },
    {
      labelKey: 'measure-unit-kilometer',
      value: CounterMeasureUnitEnum.Kilometer,
    },
    {
      labelKey: 'measure-unit-foot',
      value: CounterMeasureUnitEnum.Foot,
    },
    {
      labelKey: 'measure-unit-yard',
      value: CounterMeasureUnitEnum.Yard,
    },
    {
      labelKey: 'measure-unit-mile',
      value: CounterMeasureUnitEnum.Mile,
    },
  ];
}

export function getMeasureEnergyUnitOptions() {
  return [
    {
      labelKey: 'measure-unit-kilowatt-hour',
      value: CounterMeasureUnitEnum.KilowattHour,
    },
    {
      labelKey: 'measure-unit-water-consumption',
      value: CounterMeasureUnitEnum.CubicMeter,
    },
  ];
}

export function getMeasureMassUnitOptions() {
  return [
    {
      labelKey: 'measure-unit-gram',
      value: CounterMeasureUnitEnum.Gram,
    },
    {
      labelKey: 'measure-unit-kilogram',
      value: CounterMeasureUnitEnum.Kilogram,
    },
    {
      labelKey: 'measure-unit-ton',
      value: CounterMeasureUnitEnum.Ton,
    },
  ];
}

export function getMeasureQuantityUnitOptions() {
  return [
    {
      labelKey: 'measure-unit-piece',
      value: CounterMeasureUnitEnum.Piece,
    },
  ];
}

export function getMeasureTimeUnitOptions() {
  return [
    {
      labelKey: 'measure-unit-second',
      value: CounterMeasureUnitEnum.Second,
    },
    {
      labelKey: 'measure-unit-minute',
      value: CounterMeasureUnitEnum.Minute,
    },
    {
      labelKey: 'measure-unit-hour',
      value: CounterMeasureUnitEnum.Hour,
    },
  ];
}

export function getMeasureUnitOptionsByType(
  type: CounterMeasureTypeEnum | undefined,
) {
  if (type === CounterMeasureTypeEnum.Distance) {
    return getMeasureDistanceUnitOptions();
  }
  if (type === CounterMeasureTypeEnum.Energy) {
    return getMeasureEnergyUnitOptions();
  }
  if (type === CounterMeasureTypeEnum.Mass) {
    return getMeasureMassUnitOptions();
  }
  if (type === CounterMeasureTypeEnum.Quantity) {
    return getMeasureQuantityUnitOptions();
  }
  if (type === CounterMeasureTypeEnum.Time) {
    return getMeasureTimeUnitOptions();
  }
  return [];
}

export function getMeasureUnitOption(
  type: CounterMeasureTypeEnum,
  unit: CounterMeasureUnitEnum,
) {
  const options = getMeasureUnitOptionsByType(type);
  return options.find((o) => o.value === unit);
}

/**
 * Extracts an abbreviation from a string.
 *
 * @param input - The input string containing an abbreviation in parentheses.
 * @param removeParentheses - Optional parameter. If set to `true`, returns the abbreviation without parentheses.
 *                            Defaults to `false`, returns with parentheses.
 * @returns Extracted abbreviation either with or without parentheses. If no parentheses are found, returns an empty string.
 */
export function extractAbbreviation(
  input: string,
  removeParentheses = false,
): string {
  // Updated regular expression to handle nested and empty parentheses
  const regex = /\(+([^)]*)\)+/;
  const match = input.match(regex);
  if (!match) {
    return '';
  }
  return removeParentheses ? match[1] : match[0];
}

export function getMeasurementUnitLabel(
  type: CounterMeasureTypeEnum,
  unit: CounterMeasureUnitEnum,
  t: TFunction<'translation', undefined>,
) {
  const unitLabelKey = getMeasureUnitOptionsByType(type).find(
    ({ value }) => value === unit,
  )?.labelKey;
  if (!unitLabelKey) return '';
  return extractAbbreviation(t(unitLabelKey));
}

export function getMeasurementLabel(
  type: CounterMeasureTypeEnum,
  unit: CounterMeasureUnitEnum,
  t: TFunction<'translation', undefined>,
) {
  const typeLabelKey = getMeasureTypeOptions().find(
    ({ value }) => value === type,
  )?.labelKey;
  const unitLabelKey = getMeasureUnitOptionsByType(type).find(
    ({ value }) => value === unit,
  )?.labelKey;
  if (!typeLabelKey || !unitLabelKey) return '';
  return `${t(typeLabelKey)} ${extractAbbreviation(t(unitLabelKey))}`;
}

export function getMeasurementAbbreviation(
  type: CounterMeasureTypeEnum | undefined,
  unit: CounterMeasureUnitEnum | undefined,
  t: TFunction<'translation', undefined>,
  removeParentheses?: boolean,
) {
  const unitLabelKey = getMeasureUnitOptionsByType(type).find(
    ({ value }) => value === unit,
  )?.labelKey;
  if (!unitLabelKey) return null;
  return extractAbbreviation(t(unitLabelKey), removeParentheses);
}
